import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeftIcon, CheckCircle2Icon, ShieldIcon, FingerprintIcon, ScanFaceIcon } from 'lucide-react';
export type MPINMode = 'enter' | 'set' | 'confirm' | 'success';
export type PinType = 'login' | 'mpin';
interface MPINScreenProps {
  mode: MPINMode;
  pinType?: PinType;
  onComplete: () => void;
  onBack: () => void;
  onModeChange?: (mode: MPINMode) => void;
}
export function MPINScreen({
  mode,
  pinType = 'login',
  onComplete,
  onBack,
  onModeChange
}: MPINScreenProps) {
  const [pin, setPin] = useState('');
  const [firstPin, setFirstPin] = useState('');
  const [error, setError] = useState(false);
  const [biometricPhase, setBiometricPhase] = useState<'idle' | 'scanning' | 'verified'>('idle');
  const [biometricType, setBiometricType] = useState<'face' | 'thumb'>('thumb');
  const handleBiometricAuth = (type: 'face' | 'thumb') => {
    setBiometricType(type);
    setBiometricPhase('scanning');
    // Simulate scanning
    setTimeout(() => {
      setBiometricPhase('verified');
      // Auto-complete after verified
      setTimeout(() => {
        setBiometricPhase('idle');
        if (mode === 'enter') {
          onComplete();
        }
      }, 800);
    }, 2000);
  };
  const pinLength = pinType === 'mpin' ? 4 : 6;
  // Handle PIN entry
  const handleNumberClick = (num: string) => {
    if (pin.length < pinLength) {
      setPin(prev => prev + num);
      setError(false);
    }
  };
  const handleDelete = () => {
    setPin(prev => prev.slice(0, -1));
    setError(false);
  };
  // Auto-submit when pin reaches required length
  useEffect(() => {
    if (pin.length === pinLength) {
      const timer = setTimeout(() => {
        if (mode === 'enter') {
          onComplete();
        } else if (mode === 'set') {
          setFirstPin(pin);
          setPin('');
          if (onModeChange) onModeChange('confirm');
        } else if (mode === 'confirm') {
          if (pin === firstPin) {
            if (onModeChange) onModeChange('success');
          } else {
            setError(true);
            setPin('');
          }
        }
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [pin, mode, firstPin, onComplete, onModeChange]);
  // Handle success screen timeout
  useEffect(() => {
    if (mode === 'success') {
      const timer = setTimeout(() => {
        onComplete();
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [mode, onComplete]);
  const label = pinType === 'mpin' ? 'MPIN' : 'Login PIN';
  const digits = pinType === 'mpin' ? '4' : '6';
  const getTitle = () => {
    switch (mode) {
      case 'enter':
        return `Enter ${label}`;
      case 'set':
        return `Set ${label}`;
      case 'confirm':
        return `Confirm ${label}`;
      case 'success':
        return 'Success!';
      default:
        return label;
    }
  };
  const getSubtitle = () => {
    switch (mode) {
      case 'enter':
        return `Enter your ${digits}-digit ${label} to continue`;
      case 'set':
        return `Create a ${digits}-digit ${label} for secure ${pinType === 'mpin' ? 'transactions' : 'access'}`;
      case 'confirm':
        return `Re-enter your ${digits}-digit ${label} to confirm`;
      case 'success':
        return `Your ${label} has been set successfully`;
      default:
        return '';
    }
  };
  if (mode === 'success') {
    return <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} className="w-full min-h-screen flex flex-col items-center justify-center py-6 px-6 relative overflow-hidden" style={{
      backgroundColor: '#1a1008'
    }} data-id="element-421">
        {/* Dark theme background accents */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden" data-id="element-422">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-[#22C55E]/10 rounded-full blur-3xl" data-id="element-423" />
        </div>

        <motion.div initial={{
        scale: 0.5,
        opacity: 0
      }} animate={{
        scale: 1,
        opacity: 1
      }} transition={{
        type: 'spring',
        damping: 15,
        stiffness: 200
      }} className="w-24 h-24 rounded-full bg-[#22C55E]/15 flex items-center justify-center mb-6 border border-[#22C55E]/30 relative z-10" data-id="element-424">
          <CheckCircle2Icon className="w-14 h-14 text-[#22C55E]" strokeWidth={1.5} data-id="element-425" />
        </motion.div>
        <motion.h1 initial={{
        y: 10,
        opacity: 0
      }} animate={{
        y: 0,
        opacity: 1
      }} transition={{
        delay: 0.2
      }} className="text-[28px] font-bold text-[#FFF7F3] text-center mb-2 relative z-10" data-id="element-426">
          Congratulations!
        </motion.h1>
        <motion.p initial={{
        y: 10,
        opacity: 0
      }} animate={{
        y: 0,
        opacity: 1
      }} transition={{
        delay: 0.3
      }} className="text-[15px] text-[#FFF7F3]/60 text-center max-w-[280px] relative z-10" data-id="element-427">
          {pinType === 'mpin' ? 'Your MPIN has been set successfully. You can now use it for secure transactions.' : 'Your Login PIN has been set successfully. You can now use it to login securely.'}
        </motion.p>
      </motion.div>;
  }
  return <motion.div initial={{
    opacity: 0,
    x: 20
  }} animate={{
    opacity: 1,
    x: 0
  }} exit={{
    opacity: 0,
    x: -20
  }} className="w-full min-h-screen flex flex-col py-6 px-6 relative overflow-hidden" style={{
    backgroundColor: '#1a1008'
  }} data-id="element-428">
      {/* Dark theme background accents */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden" data-id="element-429">
        <div className="absolute top-0 left-0 w-80 h-80 bg-[#661806]/20 rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2" data-id="element-430" />
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-[#F37024]/10 rounded-full blur-3xl translate-x-1/3 translate-y-1/3" data-id="element-431" />
      </div>

      {/* Header */}
      <div className="flex items-center justify-between mb-12 relative z-10" data-id="element-432">
        <button onClick={onBack} className="p-2 -ml-2 rounded-full hover:bg-white/5 transition-colors" data-id="element-433">
          <ArrowLeftIcon className="w-6 h-6 text-[#FFF7F3]" strokeWidth={2} data-id="element-434" />
        </button>
        <img src="https://cdn.magicpatterns.com/uploads/k5mzauqmFki77bNt7CFYh1/Whatsapp_digiBOP-removebg-preview.png" alt="digiBOP Logo" className="h-8 w-auto" data-id="element-435" />
        <div className="w-10" data-id="element-436" />
      </div>

      {/* Title */}
      <div className="flex flex-col items-center mb-12 text-center relative z-10" data-id="element-437">
        <div className="w-16 h-16 rounded-full bg-[#FFF7F3]/5 border border-[#FFF7F3]/10 flex items-center justify-center mb-6" data-id="element-438">
          <ShieldIcon className="w-8 h-8 text-[#F37024]" strokeWidth={1.5} data-id="element-439" />
        </div>
        <h1 className="text-[28px] font-bold text-[#FFF7F3] leading-tight mb-2 tracking-tight" data-id="element-440">
          {getTitle()}
        </h1>
        <p className={`text-[15px] ${error ? 'text-red-400 font-medium' : 'text-[#FFF7F3]/60'}`} data-id="element-441">
          {error ? 'PINs do not match. Try again.' : getSubtitle()}
        </p>
      </div>

      {/* PIN Indicators */}
      <div className="flex justify-center gap-3 mb-8 relative z-10" data-id="element-442">
        {Array.from({
        length: pinLength
      }, (_, index) => index).map(index => <div key={index} className={`w-4 h-4 rounded-full transition-all duration-200 ${index < pin.length ? 'bg-[#F37024] scale-110 border-transparent' : 'bg-[#FFF7F3]/5 border border-[#FFF7F3]/15'} ${error ? 'bg-red-500 border-red-500' : ''}`} data-id="element-443" />)}
      </div>

      {/* Biometric Options - only show in 'enter' mode */}
      {mode === 'enter' && <div className="flex flex-col items-center mb-8 relative z-10" data-id="element-444">
          <AnimatePresence mode="wait" data-id="element-445">
            {biometricPhase === 'idle' && <motion.div key="idle" initial={{
          opacity: 0,
          y: 8
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -8
        }} className="flex flex-col items-center gap-3" data-id="element-446">
                <p className="text-[12px] font-bold text-[#FFF7F3]/40 uppercase tracking-wider mb-2" data-id="element-447">
                  Or verify with
                </p>
                <div className="flex items-center gap-5" data-id="element-448">
                  <button onClick={() => handleBiometricAuth('thumb')} className="flex flex-col items-center gap-2 group" data-id="element-449">
                    <div className="w-14 h-14 rounded-full bg-[#FFF7F3]/5 border border-[#FFF7F3]/10 flex items-center justify-center group-hover:border-[#F37024]/50 group-hover:bg-[#F37024]/10 transition-all group-active:scale-95" data-id="element-450">
                      <FingerprintIcon className="w-7 h-7 text-[#F37024]" strokeWidth={1.5} data-id="element-451" />
                    </div>
                    <span className="text-[11px] font-semibold text-[#FFF7F3]/60" data-id="element-452">
                      Fingerprint
                    </span>
                  </button>
                  <div className="w-px h-12 bg-[#FFF7F3]/10" data-id="element-453" />
                  <button onClick={() => handleBiometricAuth('face')} className="flex flex-col items-center gap-2 group" data-id="element-454">
                    <div className="w-14 h-14 rounded-full bg-[#FFF7F3]/5 border border-[#FFF7F3]/10 flex items-center justify-center group-hover:border-[#F37024]/50 group-hover:bg-[#F37024]/10 transition-all group-active:scale-95" data-id="element-455">
                      <ScanFaceIcon className="w-7 h-7 text-[#F37024]" strokeWidth={1.5} data-id="element-456" />
                    </div>
                    <span className="text-[11px] font-semibold text-[#FFF7F3]/60" data-id="element-457">
                      Face Lock
                    </span>
                  </button>
                </div>
              </motion.div>}

            {biometricPhase === 'scanning' && <motion.div key="scanning" initial={{
          opacity: 0,
          scale: 0.9
        }} animate={{
          opacity: 1,
          scale: 1
        }} exit={{
          opacity: 0,
          scale: 0.9
        }} className="flex flex-col items-center gap-3" data-id="element-458">
                <motion.div animate={{
            scale: [1, 1.15, 1],
            opacity: [0.7, 1, 0.7]
          }} transition={{
            duration: 1.2,
            repeat: Infinity,
            ease: 'easeInOut'
          }} className="w-16 h-16 rounded-full bg-[#F37024]/15 border border-[#F37024]/30 flex items-center justify-center" data-id="element-459">
                  {biometricType === 'thumb' ? <FingerprintIcon className="w-8 h-8 text-[#F37024]" strokeWidth={1.5} data-id="element-460" /> : <ScanFaceIcon className="w-8 h-8 text-[#F37024]" strokeWidth={1.5} data-id="element-461" />}
                </motion.div>
                <p className="text-[13px] font-semibold text-[#F37024]" data-id="element-462">
                  {biometricType === 'thumb' ? 'Scanning fingerprint...' : 'Scanning face...'}
                </p>
              </motion.div>}

            {biometricPhase === 'verified' && <motion.div key="verified" initial={{
          opacity: 0,
          scale: 0.9
        }} animate={{
          opacity: 1,
          scale: 1
        }} exit={{
          opacity: 0,
          scale: 0.9
        }} className="flex flex-col items-center gap-3" data-id="element-463">
                <motion.div initial={{
            scale: 0.5
          }} animate={{
            scale: 1
          }} transition={{
            type: 'spring',
            damping: 12,
            stiffness: 200
          }} className="w-16 h-16 rounded-full bg-[#22C55E]/15 border border-[#22C55E]/30 flex items-center justify-center" data-id="element-464">
                  <CheckCircle2Icon className="w-8 h-8 text-[#22C55E]" strokeWidth={1.5} data-id="element-465" />
                </motion.div>
                <p className="text-[13px] font-semibold text-[#22C55E]" data-id="element-466">
                  Verified successfully!
                </p>
              </motion.div>}
          </AnimatePresence>
        </div>}

      {/* Numpad */}
      <div className="mt-auto pb-8 relative z-10" data-id="element-467">
        <div className="grid grid-cols-3 gap-y-6 gap-x-4 max-w-[280px] mx-auto" data-id="element-468">
          {[1, 2, 3, 4, 5, 6, 7, 8, 9].map(num => <button key={num} onClick={() => handleNumberClick(num.toString())} disabled={biometricPhase !== 'idle'} className="w-16 h-16 mx-auto rounded-full flex items-center justify-center text-[28px] font-medium text-[#FFF7F3] hover:bg-[#FFF7F3]/10 active:bg-[#FFF7F3]/20 transition-colors disabled:opacity-40 disabled:pointer-events-none" data-id="element-469">
              {num}
            </button>)}
          <div data-id="element-470" /> {/* Empty space */}
          <button onClick={() => handleNumberClick('0')} disabled={biometricPhase !== 'idle'} className="w-16 h-16 mx-auto rounded-full flex items-center justify-center text-[28px] font-medium text-[#FFF7F3] hover:bg-[#FFF7F3]/10 active:bg-[#FFF7F3]/20 transition-colors disabled:opacity-40 disabled:pointer-events-none" data-id="element-471">
            0
          </button>
          <button onClick={handleDelete} disabled={biometricPhase !== 'idle'} className="w-16 h-16 mx-auto rounded-full flex items-center justify-center text-[#FFF7F3] hover:bg-[#FFF7F3]/10 active:bg-[#FFF7F3]/20 transition-colors disabled:opacity-40 disabled:pointer-events-none" data-id="element-472">
            <ArrowLeftIcon className="w-7 h-7" strokeWidth={2} data-id="element-473" />
          </button>
        </div>
      </div>
    </motion.div>;
}